/**
 * BulkReassignModal — pick a recruiter (or "release to team pool") and
 * apply owner change to N leads in one shot. V2.1.1 iter183.
 *
 * Admin/superadmin only on the backend; frontend still shows the button
 * to all recruiter roles and lets the API return 403 if unauthorised —
 * simpler than duplicating role logic here.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import { X, UserSwitch, Users } from "@phosphor-icons/react";

export default function BulkReassignModal({ open, leadIds, onClose, onDone }) {
    const [recruiters, setRecruiters] = useState([]);
    const [target, setTarget] = useState(""); // user id or "" (= team pool)
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        api.get("/crm/recruiters")
            .then((r) => setRecruiters(r.data?.recruiters || []))
            .catch((e) => {
                logError("recruiter list failed", e);
                toast.error("Couldn't load recruiter list");
            })
            .finally(() => setLoading(false));
    }, [open]);

    async function submit() {
        setSaving(true);
        try {
            const r = await api.post("/crm/bulk/leads/reassign", {
                lead_ids: leadIds,
                new_owner_id: target || null,
            });
            toast.success(
                target
                    ? `Reassigned ${r.data?.modified || 0} lead(s)`
                    : `Released ${r.data?.modified || 0} lead(s) to team pool`,
            );
            onDone?.();
        } catch (e) {
            logError("bulk reassign failed", e);
            toast.error(e?.response?.data?.detail || "Bulk reassign failed");
        } finally {
            setSaving(false);
        }
    }

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={onClose}
            data-testid="crm-bulk-reassign-modal"
        >
            <div
                className="w-full max-w-md bg-white border-2 border-ink p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                            Bulk action · {leadIds.length} lead{leadIds.length === 1 ? "" : "s"}
                        </p>
                        <h3 className="font-heading font-black text-lg mt-1 flex items-center gap-2">
                            <UserSwitch size={18} weight="bold" /> Reassign
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="p-1 hover:bg-neutral-100"
                        data-testid="crm-bulk-reassign-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <p className="text-xs text-neutral-600 mb-3">
                    Pick a new owner or release the leads back to the team pool.
                </p>

                <div className="space-y-2 max-h-64 overflow-y-auto border border-neutral-200">
                    <label
                        className={`flex items-center gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${target === "" ? "bg-cobalt/5" : ""}`}
                    >
                        <input
                            type="radio"
                            name="reassign-target"
                            value=""
                            checked={target === ""}
                            onChange={() => setTarget("")}
                            data-testid="crm-bulk-reassign-team-pool"
                            className="accent-cobalt"
                        />
                        <Users size={14} weight="bold" className="text-cobalt" />
                        <span className="text-sm font-bold">Team pool</span>
                        <span className="text-[10px] text-neutral-500 ml-auto">unassigned</span>
                    </label>
                    {loading && (
                        <p className="text-xs text-neutral-500 px-3 py-2">Loading recruiters…</p>
                    )}
                    {!loading && recruiters.map((rec) => (
                        <label
                            key={rec.id}
                            className={`flex items-center gap-2 px-3 py-2 border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${target === rec.id ? "bg-cobalt/5" : ""}`}
                        >
                            <input
                                type="radio"
                                name="reassign-target"
                                value={rec.id}
                                checked={target === rec.id}
                                onChange={() => setTarget(rec.id)}
                                data-testid={`crm-bulk-reassign-target-${rec.id.slice(0, 8)}`}
                                className="accent-cobalt"
                            />
                            <span className="text-sm font-bold text-ink">{rec.name || rec.email}</span>
                            <span className="text-[10px] font-mono uppercase text-neutral-500 ml-auto">
                                {rec.role}
                            </span>
                        </label>
                    ))}
                </div>

                <div className="flex justify-end gap-2 mt-5">
                    <button
                        onClick={onClose}
                        className="border-2 border-ink px-4 py-2 text-xs font-bold uppercase"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || loading}
                        data-testid="crm-bulk-reassign-submit"
                        className="bg-ink text-white px-4 py-2 text-xs font-bold uppercase disabled:opacity-40"
                    >
                        {saving ? "Saving…" : "Apply"}
                    </button>
                </div>
            </div>
        </div>
    );
}
